import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted } from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import VModal from "@/components/vModal.vue";
import VTable from "@/components/vTable.vue";
import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import RecipeCostRepository from "@/utils/API/RecipeCostRepository";
import {RecipeCost} from "@/models/RecipeCost/RecipeCost";
import {dateTimeDisplayFormat} from "@/utils/Defaults";
import moment from "moment";
import router from "@/router";


export default /*#__PURE__*/_defineComponent({
  __name: 'vListRecipeCost',
  props: {
  showListRecipeCostModal: {
    type: Boolean,
    required: true,
  },
  recipeCostIdsInInvoice: {
    type: Array<number>,
    required: true,
  },
},
  emits: ["on-cancel", "on-add", "on-remove"],
  setup(__props, { emit }) {

const props = __props;



let translations = useTranslations();


const filter = ref<Filter[]>([
  { name: "", value: "" },
]);

const ordering = ref<Ordering>({
  field: "",
  orderType: OrderType.Asc,
});

const recipeCosts = ref<RecipeCost[]>([]);
const totalCount = ref(0);
const count = ref(0);
const pageSize = ref(0);
const page = ref(1);
const loading = ref(false);

const deletingModal = reactive({
  isOpen: false,
  id: 0,
});
const searchTerm = ref("");
const tableHeaders = computed(() => [
  {
    name: translate("rec-cost-table-name"),
    key: "RecipeName",
    searchable: false,
    searchString: "",
    sortable: true,
  },
  {
    name: translate("rec-cost-table-total-cost"),
    key: "TotalRecipeCost",
    searchable: false,
    searchString: "",
    sortable: true,
  },
  {
    name: translate("rec-cost-table-date"),
    key: "DateCreated",
    searchable: false,
    searchString: "",
    sortable: true,
  },
  {
    name: translate("rec-cost-table-notif"),
    key: "Notification",
    searchable: false,
    searchString: "",
    sortable: false,
  },
  { name: translate("rec_invoice_add_new_cost"), key: "Add", searchable: false, searchString: "", sortable: true },
]);

const filteredRecipeCosts = computed(() => {
  const filtered = recipeCosts.value.filter((x) =>
    x.recipe.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  );

  // Sort so that recipeCostIdsInInvoice come first
  return filtered.sort((a, b) => {
    const aInInvoice = props.recipeCostIdsInInvoice.includes(a.id);
    const bInInvoice = props.recipeCostIdsInInvoice.includes(b.id);

    if (aInInvoice === bInInvoice) return 0;
    return aInInvoice ? -1 : 1;
  });
});

const hasRecipeCosts = computed(() => recipeCosts.value.length > 0);

const recipeCostsForGrid = computed(() =>
  filteredRecipeCosts.value.map((x) => {
    const isSelected = props.recipeCostIdsInInvoice.includes(x.id);
    let notificationComponent: any = "";

    if (
      !x.isAnyIngredientCostUpdated &&
      !x.isAnyIngredientCostDeleted &&
      x.isAnySubRecipeCostUpdated
    ) {
      notificationComponent = {
        component: "vImageWithTooltip",
        props: {
          imgSrc: require("@/assets/img/customer-facing/icons/ingredient-cost-updated.png"),
          tooltipContent: translate("sub-recipe-cost-updated-msg"),
          tooltipPlacement: "bottom-start",
          imgStyle: "height: 25px; margin-left: 2px;",
        },
      };
    }
    else if (x.isAnyIngredientCostUpdated || x.isAnySubRecipeCostUpdated) {
      notificationComponent = {
        component: "vImageWithTooltip",
        props: {
          imgSrc: require("@/assets/img/customer-facing/icons/ingredient-cost-updated.png"),
          tooltipContent: translate("rec-cost-updated-msg"),
          tooltipPlacement: "bottom-start",
          imgStyle: "height: 25px; margin-left: 2px;",
        },
      };
    }
    else if (x.isAnyIngredientCostDeleted) {
      notificationComponent = {
        component: "vImageWithTooltip",
        props: {
          imgSrc: require("@/assets/img/customer-facing/icons/ingredient-cost-deleted.png"),
          tooltipContent: translate("rec-cost-deleted-msg"),
          tooltipPlacement: "bottom-start",
          imgStyle: "height: 22px; margin-left: 4px;",
        },
      };
    }

    return {
      meta: {
        id: x.id,
        link: x.recipe.name,
      },
      e1: (Math.round(x.totalCost * 100) / 100).toFixed(2),
      e2: moment(x.createdOn).format(dateTimeDisplayFormat),
      e3: notificationComponent,
      e4: {
        component: "a",
        html: isSelected
          ? `<i class="fa fa-check-circle" style="font-size:30px; color: green"></i>`
          : `<i class="fa fa-plus-circle text-blue" style="font-size:30px;"></i>`,
        props: {
          href: "#",
          "data-target": "addIngredientCost",
          "data-toggle": "modal",
        },
        onClick: () =>
          isSelected
            ? removeRecipeCostFromInvoice(x.id)
            : addRecipeCostToInvoice(x.id),
      },
    };
  })
);

function translate(key: string) {
  return translations.value.get(key);
}

function closeModal() {
  emit("on-cancel");
}

function addRecipeCostToInvoice(id: number) {
  emit("on-add", id);
  closeModal();
}

function removeRecipeCostFromInvoice(id: number){
  emit("on-remove", id);
  closeModal();
}

async function fetchRecipeCosts() {
  const orderings = ordering.value ? [ordering.value] : [];
  const reqData = new ListRequest({
    page: page.value,
    pageSize: pageSize.value,
    filters: filter.value,
    orders: orderings,
  });

  loading.value = true;
  try {
    const response = await RecipeCostRepository.listRecipeCosts(reqData);

    recipeCosts.value = response.data.result;
    totalCount.value = response.data.totalCount;
    count.value = response.data.count;
  }
  catch (err) {
    console.error("Failed to fetch recipe costs", err);
  }
  finally {
    loading.value = false;
  }
}

function redirectToRecipeCostPage(){
  router.push("/recipes");
}

function changePage(args: any) {
  const pageNum = args[0];
  const filtersFromTable = args[2];
  const orderingsFromTable = args[3];

  page.value = pageNum;
  ordering.value =
    orderingsFromTable && orderingsFromTable.length > 0
      ? orderingsFromTable[0]
      : { field: "", orderType: OrderType.Asc };

  filter.value =
    filtersFromTable && filtersFromTable.length > 0
      ? filtersFromTable
      : [];

  fetchRecipeCosts();
}

async function order(filters: Filter[], orders: Ordering) {
  filter.value = filters;
  ordering.value = orders;
  await fetchRecipeCosts();
}

function goOnEditRecipeCostPage(recipeCostId: number) {
  router.push({
    name: "updateRecipeCost",
    params: { id: recipeCostId },
  });
}

function deleteRecipeCost(id: number) {
  const recCost = recipeCosts.value.find((x) => x.id === id);
  if (recCost) {
    deletingModal.isOpen = true;
    deletingModal.id = recCost.id;
  }
}

onMounted(() => {
  fetchRecipeCosts();
});

return { __sfc: true,props, translations, emit, filter, ordering, recipeCosts, totalCount, count, pageSize, page, loading, deletingModal, searchTerm, tableHeaders, filteredRecipeCosts, hasRecipeCosts, recipeCostsForGrid, translate, closeModal, addRecipeCostToInvoice, removeRecipeCostFromInvoice, fetchRecipeCosts, redirectToRecipeCostPage, changePage, order, goOnEditRecipeCostPage, deleteRecipeCost, VModal, VTable }
}

})